import {
  Checkbox,
  Divider,
  Dropdown,
  DropdownItem,
  PendingContent,
  Spinner,
  Status,
  TextToggler,
} from 'plume-ui';
import React, { FunctionComponent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { DependencyContainer } from '../../../../../../DependencyContainer';
import { useFeaturePermission } from '../../../../../../hooks/useFeaturePermission';
import { useFetchData } from '../../../../../../hooks/useFetchData';
import { activePartnerAtom } from '../../../../../../state';
import { Permission } from '../../../../../../userPermissionConfig';
import FormattedMessage from '../../../../../../utils/components/FormattedMessage';
import { mapWebhookStatusToColor } from '../../../../../Configuration/components/Webhook/helpers';
import { WebhookStatus } from '../../../../../Configuration/components/Webhook/types';
import {
  OutreachConfigFormFields,
  OutreachConfigGenericFormField,
  OutreachConfigSections,
} from '../../../../types';
import useFormControls from '../../hooks/useFormControls';
import useValidation from '../../hooks/useValidation';
import OutreachConfigSection from '../../OutreachConfigSection';

export type FormikOutreachConfigValue = {
  subscriberOutreach: boolean;
  subscriberOutreachEnabled: boolean;
  supportOutreach: boolean;
  supportOutreachEnabled: boolean;
  webhookOutreach: boolean;
  webhookOutreachEnabled: boolean;
  selectedWebhook: string[];
  selectedWebhookName: string;
};

const { configurationService } = new DependencyContainer();

const FormikOutreachConfigField: FunctionComponent<OutreachConfigGenericFormField<
  FormikOutreachConfigValue
>> = ({ field, form }) => {
  const { t } = useTranslation();
  const { hasPermission } = useFeaturePermission();
  const activePartner = useRecoilValue(activePartnerAtom);
  const [webhooksData, setWebhooksData] = useState<WebhookStatus[]>();
  const [dropdownSelectedItem, setDropdownSelectedItem] = useState<string>();
  const { isSectionComplete } = useValidation();
  const {
    toggleFieldValue,
    getPropertyValue,
    setPropertyValue,
  } = useFormControls<FormikOutreachConfigValue>(field, form);
  const titleCopyId = 'outreach.form.outreachConfigSection.title';
  const subtitleCopyId = 'outreach.form.outreachConfigSection.subtitle';

  const sendToSubscriberOutreach = Boolean(
    getPropertyValue(OutreachConfigFormFields.SubscriberOutreach),
  );
  const subscriberOutreachEnabled = Boolean(
    getPropertyValue(OutreachConfigFormFields.SubscriberOutreachEnabled),
  );
  const sendToSupportOutreach = Boolean(
    getPropertyValue(OutreachConfigFormFields.SupportOutreach),
  );
  const supportOutreachEnabled = Boolean(
    getPropertyValue(OutreachConfigFormFields.SupportOutreachEnabled),
  );
  const sendToWebhookOutreach = Boolean(
    getPropertyValue(OutreachConfigFormFields.WebhookOutreach),
  );
  const webhookOutreachEnabled = Boolean(
    getPropertyValue(OutreachConfigFormFields.WebhookOutreachEnabled),
  );
  const selectedWebhookLabel = getPropertyValue(
    OutreachConfigFormFields.SelectedWebhookName,
  );
  const canEnableSupportEmail = hasPermission(
    Permission.EnableSupportEmailOutreach,
  );
  const canEnableSubscriberPush = hasPermission(
    Permission.EnableSubscriberPushOutreach,
  );
  const canEditSupportEmail = hasPermission(
    Permission.EditSupportEmailOutreach,
  );
  const canEditSubscriberPush = hasPermission(
    Permission.EditSubscriberPushOutreach,
  );
  const canEditSupportWebhook = hasPermission(
    Permission.EditSupportWebhookOutreach,
  );
  const canEnableSupportWebhook = hasPermission(
    Permission.EnableSupportWebhookOutreach,
  );
  const isComplete = isSectionComplete(OutreachConfigSections.Config);

  const [loadingWebhooks] = useFetchData<WebhookStatus[], any>(
    async () => {
      if (!activePartner?.id) return;
      const listItems = await configurationService.getWebhooksListItems(
        activePartner.id,
      );
      return listItems;
    },
    (data) => setWebhooksData(data),
    [activePartner],
    () => activePartner === undefined,
  );

  useEffect(() => {
    if (webhooksData === undefined) return;
    setDropdownSelectedItem(
      getPropertyValue(OutreachConfigFormFields.SelectedWebhookName) as string,
    );
  }, [webhooksData]);

  useEffect(() => {
    if (!dropdownSelectedItem) return;
    setPropertyValue(
      OutreachConfigFormFields.SelectedWebhookName,
      dropdownSelectedItem,
    );
  }, [dropdownSelectedItem]);

  useEffect(() => {
    if (
      selectedWebhookLabel === undefined ||
      typeof selectedWebhookLabel !== 'string' ||
      webhooksData === undefined
    )
      return;
    const webhookId = webhooksData.find(
      (webhook) => webhook.name === selectedWebhookLabel,
    )?.id;
    if (webhookId === undefined) return;
    setDropdownSelectedItem(selectedWebhookLabel);
    setPropertyValue(OutreachConfigFormFields.SelectedWebhook, [webhookId]);
  }, [selectedWebhookLabel]);

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      hideComplete={
        !sendToSubscriberOutreach &&
        !sendToSupportOutreach &&
        !sendToWebhookOutreach
      }
    >
      <div className="OutreachContainer__outreach-config-label">
        <FormattedMessage id="outreach.form.outreachConfigSection.sendTo" />
      </div>
      <div className="OutreachContainer__outreach-config-fields">
        <Checkbox
          onChange={() =>
            toggleFieldValue(OutreachConfigFormFields.SubscriberOutreach)
          }
          disabled={!canEditSubscriberPush}
          checked={sendToSubscriberOutreach}
          label={t('outreach.form.outreachConfigSection.subscriberOutreach')}
        />
        {sendToSubscriberOutreach && (
          <TextToggler
            title={t(
              `outreach.form.outreachConfigSection.${
                subscriberOutreachEnabled ? 'enabled' : 'disabled'
              }`,
            )}
            disabled={!canEnableSubscriberPush}
            selected={subscriberOutreachEnabled}
            onToggle={() =>
              toggleFieldValue(
                OutreachConfigFormFields.SubscriberOutreachEnabled,
              )
            }
          />
        )}
      </div>
      <div className="OutreachContainer__outreach-config-fields">
        <Checkbox
          onChange={() =>
            toggleFieldValue(OutreachConfigFormFields.SupportOutreach)
          }
          disabled={!canEditSupportEmail}
          checked={sendToSupportOutreach}
          label={t('outreach.form.outreachConfigSection.supportOutreach')}
        />
        {sendToSupportOutreach && (
          <TextToggler
            title={t(
              `outreach.form.outreachConfigSection.${
                supportOutreachEnabled ? 'enabled' : 'disabled'
              }`,
            )}
            disabled={!canEnableSupportEmail}
            selected={supportOutreachEnabled}
            onToggle={() =>
              toggleFieldValue(OutreachConfigFormFields.SupportOutreachEnabled)
            }
          />
        )}
      </div>
      <div className="OutreachContainer__outreach-config-fields">
        <Checkbox
          onChange={() => {
            toggleFieldValue(OutreachConfigFormFields.WebhookOutreach);
          }}
          disabled={!canEnableSupportWebhook || !canEditSupportWebhook}
          checked={sendToWebhookOutreach}
          label={t(
            'outreach.form.supportOutreachMethodSection.selectedMethod.webhook',
          )}
        />
        {sendToWebhookOutreach && (
          <>
            <div className="OutreachContainer__outreach-config-section__wrapper">
              <Dropdown
                label={
                  dropdownSelectedItem ||
                  t('outreach.form.supportOutreachMethodSection.labels.webhook')
                }
                disabled={!canEditSupportWebhook}
                openInPortal
                closeOnItemClick
                noItemsWarning={t('noResults')}
                noSearchMatchMessage={t('noResults')}
              >
                <PendingContent
                  loading={loadingWebhooks && !Boolean(webhooksData)}
                  loader={Spinner}
                >
                  {webhooksData?.map((webhook: WebhookStatus) => (
                    <DropdownItem
                      key={webhook.id}
                      onClick={() => {
                        setPropertyValue(
                          OutreachConfigFormFields.SelectedWebhook,
                          [webhook.id],
                        );
                        setDropdownSelectedItem(webhook.name);
                      }}
                    >
                      <div className="OutreachContainer__outreach-config-fields__dropdown-item__name">
                        {webhook.name}
                      </div>
                      <div className="OutreachContainer__outreach-config-fields__dropdown-item__status">
                        <Status
                          color={mapWebhookStatusToColor(webhook.status)}
                        />
                        {webhook.status}
                      </div>
                    </DropdownItem>
                  ))}
                  {(!webhooksData || !webhooksData.length) && (
                    <DropdownItem disabled>
                      {t('outreach.form.outreachConfigSection.noWebhooks')}
                    </DropdownItem>
                  )}
                </PendingContent>
              </Dropdown>
            </div>
            <TextToggler
              title={t(
                `outreach.form.outreachConfigSection.${
                  webhookOutreachEnabled ? 'enabled' : 'disabled'
                }`,
              )}
              disabled={!canEnableSupportWebhook}
              selected={webhookOutreachEnabled}
              onToggle={() =>
                toggleFieldValue(
                  OutreachConfigFormFields.WebhookOutreachEnabled,
                )
              }
            />
          </>
        )}
      </div>
      <Divider />
      <p className="OutreachContainer__outreach-config-hint">
        <FormattedMessage id="outreach.form.outreachConfigSection.hint" />
      </p>
    </OutreachConfigSection>
  );
};

export default memo(FormikOutreachConfigField);
